import { PermissionCode, FleetAreaCode, type FleetAssignment } from '@gb-ticket/shared-types';
import { type FormEvent, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { formatDeDate } from '@/products/fleet/features/fleet-labels';
import { FleetChargeLevel } from '@/products/fleet/features/fleet-charge-level';
import { FleetVehicleChargeLine } from '@/products/fleet/features/fleet-charge-status';
import { fleetPanelClassName } from '@/products/fleet/features/fleet-panel';
import { fleetAreaOn, useFleetAreas } from '@/products/fleet/features/use-fleet-areas';
import {
  useCreateFleetPoolBooking,
  useDeleteFleetPoolBooking,
  useEndFleetPoolBookingEarly,
  useFleetAssignments,
  useFleetMeAssignments,
  useFleetPoolVehicles,
  useFleetChargeVehicle,
  useUpdateFleetPoolBooking,
} from '@/products/fleet/features/use-fleet';
import { FLEET_VEHICLES_PATH } from '@/products/fleet/fleet-paths';

function FleetBookCharge({
  vehicleId,
  plate,
  enabled,
}: {
  vehicleId: string;
  plate: string;
  enabled: boolean;
}) {
  const charge = useFleetChargeVehicle(vehicleId, enabled);
  return (
    <div className="space-y-2">
      <FleetChargeLevel
        presentation="tile"
        socPercent={enabled ? (charge.data?.socPercent ?? null) : null}
        plate={plate}
      />
      {enabled ? (
        <FleetVehicleChargeLine
          charge={charge.data}
          failedAt={charge.isError ? charge.dataUpdatedAt : null}
        />
      ) : null}
    </div>
  );
}

function assignmentOverlaps(row: FleetAssignment, from: string, to: string): boolean {
  if (row.cancelledAt) {
    return false;
  }
  const end = row.validTo ?? '9999-12-31';
  return row.validFrom <= to && end >= from;
}

const berlinWhen = new Intl.DateTimeFormat('de-DE', {
  timeZone: 'Europe/Berlin',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

function formatBookingWhen(row: FleetAssignment): string {
  if (row.startsAt && row.endsAt) {
    return `${berlinWhen.format(new Date(row.startsAt))} – ${berlinWhen.format(new Date(row.endsAt))}`;
  }
  return `${formatDeDate(row.validFrom)}${row.validTo ? ` – ${formatDeDate(row.validTo)}` : ' – offen'}`;
}

function berlinClock(iso: string | null | undefined): string {
  if (!iso) {
    return '';
  }
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Berlin',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(iso));
  const hour = parts.find((part) => part.type === 'hour')?.value ?? '00';
  const minute = parts.find((part) => part.type === 'minute')?.value ?? '00';
  return `${hour}:${minute}`;
}

function bookingIsRunning(row: FleetAssignment, now = new Date()): boolean {
  const start = row.startsAt ? new Date(row.startsAt) : new Date(`${row.validFrom}T00:00:00`);
  const end = row.endsAt
    ? new Date(row.endsAt)
    : row.validTo
      ? new Date(`${row.validTo}T23:59:59`)
      : null;
  return start.getTime() <= now.getTime() && (end == null || end.getTime() > now.getTime());
}

export function FleetBookPage() {
  usePageTitle('Pool buchen');
  const me = useMe();
  const manage = hasPermission(me.data, PermissionCode.FLEET_VEHICLES_MANAGE);
  const vehicles = useFleetPoolVehicles();
  const areas = useFleetAreas();
  const telematicsOn = fleetAreaOn(
    areas.isError ? undefined : areas.data,
    FleetAreaCode.TELEMATICS,
  );
  const book = useCreateFleetPoolBooking();
  const updateBooking = useUpdateFleetPoolBooking();
  const endEarly = useEndFleetPoolBookingEarly();
  const removeBooking = useDeleteFleetPoolBooking();
  const [vehicleId, setVehicleId] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [validFrom, setValidFrom] = useState(new Date().toISOString().slice(0, 10));
  const [validTo, setValidTo] = useState('');
  const [fromTime, setFromTime] = useState('08:00');
  const [toTime, setToTime] = useState('17:00');
  const [error, setError] = useState<string | null>(null);
  const rangeTo = validTo || validFrom;
  const vehicleAssignments = useFleetAssignments(vehicleId);
  const mine = useFleetMeAssignments(validFrom, rangeTo);

  const occupancy = useMemo(() => {
    if (!vehicleId || !validFrom) {
      return [];
    }
    const to = validTo || validFrom;
    return (vehicleAssignments.data?.items ?? []).filter((row) =>
      assignmentOverlaps(row, validFrom, to),
    );
  }, [vehicleAssignments.data?.items, vehicleId, validFrom, validTo]);

  const myBookings = mine.data?.items ?? [];
  const poolVehicles = vehicles.data?.items ?? [];
  const selected = poolVehicles.find((vehicle) => vehicle.id === vehicleId);
  const showCharge = selected?.fuelType === 'electric' || selected?.fuelType === 'hybrid';
  const noPoolVehicles = !vehicles.isPending && !vehicles.isError && poolVehicles.length === 0;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (!vehicleId) {
      setError('Bitte ein Poolfahrzeug wählen.');
      return;
    }
    if (!validTo) {
      setError('Bitte ein Enddatum angeben.');
      return;
    }
    if (!fromTime || !toTime) {
      setError('Bitte Beginn und Ende mit Uhrzeit angeben.');
      return;
    }
    try {
      if (editingId) {
        await updateBooking.mutateAsync({
          id: editingId,
          body: { validFrom, validTo, fromTime, toTime },
        });
        toast.success('Buchung geändert.');
        setEditingId(null);
      } else {
        await book.mutateAsync({ vehicleId, validFrom, validTo, fromTime, toTime });
        toast.success('Poolfahrzeug gebucht.');
      }
      setValidTo('');
    } catch (err) {
      const message =
        err instanceof ApiRequestError
          ? err.message
          : 'Die Buchung konnte nicht gespeichert werden.';
      setError(message);
      toast.error(message);
    }
  }

  function onEdit(row: FleetAssignment) {
    setError(null);
    setEditingId(row.id);
    setVehicleId(row.vehicleId);
    setValidFrom(row.validFrom);
    setValidTo(row.validTo ?? row.validFrom);
    setFromTime(row.startsAt ? berlinClock(row.startsAt) : '08:00');
    setToTime(row.endsAt ? berlinClock(row.endsAt) : '17:00');
  }

  async function onDelete(id: string) {
    setError(null);
    try {
      await removeBooking.mutateAsync(id);
      if (editingId === id) {
        setEditingId(null);
      }
      toast.success('Buchung gelöscht.');
    } catch (err) {
      const message =
        err instanceof ApiRequestError ? err.message : 'Die Buchung konnte nicht gelöscht werden.';
      setError(message);
      toast.error(message);
    }
  }

  async function onEndEarly(id: string) {
    setError(null);
    try {
      await endEarly.mutateAsync(id);
      toast.success('Buchung vorzeitig beendet.');
    } catch (err) {
      const message =
        err instanceof ApiRequestError
          ? err.message
          : 'Die Buchung konnte nicht vorzeitig beendet werden.';
      setError(message);
      toast.error(message);
    }
  }

  return (
    <div className="space-y-6">
      <PlatformPageHeader
        title="Pool buchen"
        description="Poolfahrzeug für Datum und Uhrzeit reservieren. Eine Buchung lässt sich ändern, vorzeitig beenden oder löschen."
      />
      {vehicles.isPending ? (
        <p className="text-sm text-muted-foreground">Poolfahrzeuge werden geladen …</p>
      ) : null}
      {vehicles.isError ? (
        <p className="text-sm text-destructive">
          {vehicles.error instanceof ApiRequestError
            ? vehicles.error.message
            : 'Poolfahrzeuge konnten nicht geladen werden.'}
        </p>
      ) : null}
      {noPoolVehicles ? (
        <div className={`${fleetPanelClassName} space-y-3`}>
          <p className="text-sm text-muted-foreground">
            Es gibt kein Poolfahrzeug. Die Nutzungsart steht im Fahrzeugstamm.
          </p>
          {manage ? (
            <Button asChild size="sm" variant="outline">
              <Link to={FLEET_VEHICLES_PATH}>Fahrzeuge öffnen</Link>
            </Button>
          ) : null}
        </div>
      ) : null}
      {poolVehicles.length > 0 ? (
        <form className={`${fleetPanelClassName} space-y-4`} onSubmit={onSubmit}>
          <div className="space-y-1">
            <Label htmlFor="pool-vehicle">Fahrzeug</Label>
            <Select
              value={vehicleId || 'none'}
              onValueChange={(value) => setVehicleId(value === 'none' ? '' : value)}
            >
              <SelectTrigger id="pool-vehicle" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Bitte wählen</SelectItem>
                {poolVehicles.map((vehicle) => (
                  <SelectItem key={vehicle.id} value={vehicle.id}>
                    {vehicle.licensePlate} · {vehicle.make} {vehicle.model}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {selected && showCharge ? (
            <FleetBookCharge
              vehicleId={selected.id}
              plate={selected.licensePlate}
              enabled={telematicsOn}
            />
          ) : null}
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="pool-from">Von</Label>
              <Input
                id="pool-from"
                type="date"
                value={validFrom}
                onChange={(event) => setValidFrom(event.target.value)}
                required
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="pool-to">Bis</Label>
              <Input
                id="pool-to"
                type="date"
                value={validTo}
                onChange={(event) => setValidTo(event.target.value)}
                required
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="pool-from-time">Von Uhrzeit</Label>
              <Input
                id="pool-from-time"
                type="time"
                value={fromTime}
                onChange={(event) => setFromTime(event.target.value)}
                required
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="pool-to-time">Bis Uhrzeit</Label>
              <Input
                id="pool-to-time"
                type="time"
                value={toTime}
                onChange={(event) => setToTime(event.target.value)}
                required
              />
            </div>
          </div>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
          <div className="flex flex-wrap gap-2">
            <Button
              type="submit"
              disabled={book.isPending || updateBooking.isPending || !vehicleId}
            >
              {editingId ? 'Speichern' : 'Buchen'}
            </Button>
            {editingId ? (
              <Button type="button" variant="outline" onClick={() => setEditingId(null)}>
                Abbrechen
              </Button>
            ) : null}
          </div>
        </form>
      ) : null}
      {vehicleId ? (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Belegung im gewählten Zeitraum</CardTitle>
          </CardHeader>
          <CardContent>
            {vehicleAssignments.isPending ? (
              <p className="text-sm text-muted-foreground">Wird geladen …</p>
            ) : null}
            {occupancy.length === 0 && !vehicleAssignments.isPending ? (
              <p className="text-sm text-muted-foreground">Keine Zuordnung in diesem Zeitraum.</p>
            ) : null}
            {occupancy.length > 0 ? (
              <ul className="divide-y text-sm">
                {occupancy.map((row) => (
                  <li
                    key={row.id}
                    className="flex flex-col gap-0.5 py-2 sm:flex-row sm:justify-between"
                  >
                    <span>{row.displayName ?? 'Unbekannt'}</span>
                    <span className="text-muted-foreground">{formatBookingWhen(row)}</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </CardContent>
        </Card>
      ) : null}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Meine Buchungen</CardTitle>
        </CardHeader>
        <CardContent>
          {mine.isPending ? <p className="text-sm text-muted-foreground">Wird geladen …</p> : null}
          {myBookings.length === 0 && !mine.isPending ? (
            <p className="text-sm text-muted-foreground">
              Keine eigenen Buchungen in diesem Zeitraum.
            </p>
          ) : null}
          {myBookings.length > 0 ? (
            <ul className="divide-y text-sm">
              {myBookings.map((row) => (
                <li
                  key={row.id}
                  className="flex flex-col gap-2 py-2 sm:flex-row sm:items-center sm:justify-between"
                >
                  <span className="font-medium">{row.licensePlate ?? 'Fahrzeug'}</span>
                  <span className="text-muted-foreground">{formatBookingWhen(row)}</span>
                  <span className="flex flex-wrap gap-2">
                    <Button type="button" size="sm" variant="outline" onClick={() => onEdit(row)}>
                      Bearbeiten
                    </Button>
                    {bookingIsRunning(row) ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={endEarly.isPending}
                        onClick={() => void onEndEarly(row.id)}
                      >
                        Vorzeitig beenden
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={removeBooking.isPending}
                      onClick={() => void onDelete(row.id)}
                    >
                      Löschen
                    </Button>
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
